import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatNumber } from '../lib/format';

interface HubKey {
  id: string;
  name: string;
  environment: string;
  display: string;
  active: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
}

interface ClientApp {
  id: string;
  key: string;
  name: string;
  environment: string;
  accessMode: 'ALL' | 'RESTRICTED';
  isActive: boolean;
  maxRpm: number | null;
  maxConcurrent: number | null;
  grantCount: number;
  requestCount: number;
  keys: HubKey[];
}

export function ClientAppsPage({ role }: { role: string }) {
  const apps = useResource<{ items: ClientApp[] }>('/api/admin/client-apps');
  const operations = useResource<{ items: Array<{ operationKey: string }> }>(
    '/api/admin/operations',
  );
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    key: '',
    name: '',
    environment: 'test',
    accessMode: 'ALL',
    maxRpm: '',
    maxConcurrent: '',
  });
  const [grantFor, setGrantFor] = useState<ClientApp | null>(null);
  const [grantKey, setGrantKey] = useState('');
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  const grants = useResource<{
    items: Array<{ id: string; grantType: string; targetKey: string; allowWrite: boolean }>;
  }>(grantFor ? `/api/admin/access-grants?clientAppId=${grantFor.id}` : null);

  return (
    <>
      <PageHeader
        title="Aplikacje klienckie"
        description="Aplikacja zna wyłącznie adres Hub i własny klucz. Tryb ALL daje dostęp do wszystkich włączonych operacji; RESTRICTED wymaga jawnych uprawnień."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Dodaj aplikację
            </Button>
          ) : undefined
        }
      />

      {apps.loading ? (
        <Skeleton rows={5} />
      ) : (
        <Card tight>
          <DataTable
            rows={apps.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak aplikacji"
                description="Utwórz aplikację, aby wydać jej klucz Hub."
              />
            }
            columns={[
              {
                key: 'app',
                header: 'Aplikacja',
                render: (row) => (
                  <>
                    <span className="ah-mono">{row.key}</span>
                    <div style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>{row.name}</div>
                  </>
                ),
              },
              {
                key: 'env',
                header: 'Środowisko',
                render: (row) => (
                  <Badge tone={row.environment === 'production' ? 'danger' : 'neutral'}>
                    {row.environment}
                  </Badge>
                ),
              },
              {
                key: 'access',
                header: 'Dostęp',
                render: (row) => (
                  <>
                    <Badge tone={row.accessMode === 'ALL' ? 'warn' : 'ok'}>{row.accessMode}</Badge>
                    {row.accessMode === 'RESTRICTED' && (
                      <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                        {row.grantCount} uprawnień
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'limits',
                header: 'Limity',
                render: (row) => (
                  <div style={{ fontSize: 12 }}>
                    <div>{row.maxRpm ? `${row.maxRpm} rpm` : 'rpm: domyślny'}</div>
                    <div>
                      {row.maxConcurrent
                        ? `${row.maxConcurrent} równoległych`
                        : 'równoległość: domyślna'}
                    </div>
                  </div>
                ),
              },
              {
                key: 'keys',
                header: 'Klucze',
                render: (row) => (
                  <div style={{ fontSize: 12 }}>
                    {row.keys.length === 0 ? <Badge tone="warn">brak</Badge> : null}
                    {row.keys.slice(0, 3).map((key) => (
                      <div key={key.id}>
                        <Badge tone={key.active ? 'ok' : 'neutral'}>
                          {key.active ? 'aktywny' : 'nieaktywny'}
                        </Badge>{' '}
                        <span className="ah-mono">{key.display}</span>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: 'requests',
                header: 'Żądania',
                align: 'right',
                render: (row) => formatNumber(row.requestCount),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  canWrite ? (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.patch(`/api/admin/client-apps/${row.id}`, {
                              accessMode: row.accessMode === 'ALL' ? 'RESTRICTED' : 'ALL',
                            });
                            toast.success('Tryb dostępu zmieniony');
                            apps.reload();
                          } catch (error) {
                            const described = describeError(error);
                            toast.error(described.title, described.body);
                          }
                        }}
                      >
                        {row.accessMode === 'ALL' ? 'Ogranicz' : 'Pełny dostęp'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setGrantFor(row);
                          setGrantKey('');
                        }}
                      >
                        Uprawnienia
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowa aplikacja kliencka"
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.key || !form.name}
              onClick={async () => {
                try {
                  await api.post('/api/admin/client-apps', {
                    key: form.key,
                    name: form.name,
                    environment: form.environment,
                    accessMode: form.accessMode,
                    ...(form.maxRpm ? { maxRpm: Number(form.maxRpm) } : {}),
                    ...(form.maxConcurrent ? { maxConcurrent: Number(form.maxConcurrent) } : {}),
                  });
                  toast.success('Aplikacja utworzona', 'Wydaj jej klucz na ekranie „Klucze Hub”.');
                  setOpen(false);
                  setForm({
                    key: '',
                    name: '',
                    environment: 'test',
                    accessMode: 'ALL',
                    maxRpm: '',
                    maxConcurrent: '',
                  });
                  apps.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz
            </Button>
          </>
        }
      >
        <Field label="Klucz aplikacji" hint="Identyfikator w logach, np. „sklep-web”.">
          {(id) => (
            <TextInput
              id={id}
              value={form.key}
              onChange={(event) => setForm({ ...form, key: event.target.value })}
            />
          )}
        </Field>
        <Field label="Nazwa">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field label="Środowisko">
          {(id) => (
            <Select
              id={id}
              value={form.environment}
              onChange={(event) => setForm({ ...form, environment: event.target.value })}
            >
              <option value="test">test</option>
              <option value="staging">staging</option>
              <option value="production">production</option>
            </Select>
          )}
        </Field>
        <Field
          label="Tryb dostępu"
          hint="ALL to wygodny domyślny start; RESTRICTED jest w pełni egzekwowany."
        >
          {(id) => (
            <Select
              id={id}
              value={form.accessMode}
              onChange={(event) => setForm({ ...form, accessMode: event.target.value })}
            >
              <option value="ALL">ALL — wszystkie włączone operacje</option>
              <option value="RESTRICTED">RESTRICTED — tylko przyznane</option>
            </Select>
          )}
        </Field>
        <div className="ah-grid cols-2">
          <Field label="Limit RPM">
            {(id) => (
              <TextInput
                id={id}
                type="number"
                value={form.maxRpm}
                onChange={(event) => setForm({ ...form, maxRpm: event.target.value })}
              />
            )}
          </Field>
          <Field label="Limit równoległości">
            {(id) => (
              <TextInput
                id={id}
                type="number"
                value={form.maxConcurrent}
                onChange={(event) => setForm({ ...form, maxConcurrent: event.target.value })}
              />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={grantFor !== null}
        onOpenChange={(open2) => !open2 && setGrantFor(null)}
        title="Uprawnienia aplikacji"
        description={grantFor ? `${grantFor.key} — tryb ${grantFor.accessMode}` : ''}
      >
        {grantFor?.accessMode === 'ALL' && (
          <Callout tone="warn">
            Aplikacja działa w trybie ALL, więc uprawnienia poniżej nie ograniczają dostępu.
            Przełącz ją na RESTRICTED, aby lista zaczęła obowiązywać.
          </Callout>
        )}
        <DataTable
          rows={grants.data?.items ?? []}
          rowKey={(row) => row.id}
          empty={<EmptyState title="Brak przyznanych uprawnień" />}
          columns={[
            {
              key: 'type',
              header: 'Typ',
              render: (row) => <Badge tone="info">{row.grantType}</Badge>,
            },
            {
              key: 'target',
              header: 'Cel',
              render: (row) => <span className="ah-mono">{row.targetKey}</span>,
            },
            {
              key: 'write',
              header: 'Zapis',
              render: (row) => (row.allowWrite ? <Badge tone="warn">tak</Badge> : 'nie'),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              render: (row) => (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={async () => {
                    try {
                      await api.delete(`/api/admin/access-grants/${row.id}`);
                      grants.reload();
                      apps.reload();
                    } catch (error) {
                      const described = describeError(error);
                      toast.error(described.title, described.body);
                    }
                  }}
                >
                  Usuń
                </Button>
              ),
            },
          ]}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
          <Field label="Dodaj operację">
            {(id) => (
              <Select
                id={id}
                value={grantKey}
                onChange={(event) => setGrantKey(event.target.value)}
              >
                <option value="">— wybierz operację —</option>
                {(operations.data?.items ?? []).map((operation) => (
                  <option key={operation.operationKey} value={operation.operationKey}>
                    {operation.operationKey}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            variant="primary"
            disabled={!grantKey}
            onClick={async () => {
              if (!grantFor) return;
              try {
                await api.post('/api/admin/access-grants', {
                  subjectType: 'CLIENT_APP',
                  clientAppId: grantFor.id,
                  grantType: 'OPERATION',
                  targetKey: grantKey,
                });
                setGrantKey('');
                grants.reload();
                apps.reload();
              } catch (error) {
                const described = describeError(error);
                toast.error(described.title, described.body);
              }
            }}
          >
            Przyznaj
          </Button>
        </div>
      </Modal>
    </>
  );
}
